import { Component, effect, OnDestroy, OnInit, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardPaymentFormComponent, CardFormValue } from '../../shared/card-payment-form/card-payment-form.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SubscriptionService } from '../../services/subscription.service';
import { friendlyError } from '../../utils/friendly-error';
import { AuthService } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { Subscription, SubscriptionPayment } from '../../models/sala.model';
import { ManagementRecordCardComponent } from '../ui/management-record-card.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { PaginationComponent } from '../ui/pagination.component';
import { ThemeService } from '../../services/theme.service';
import { toneForTheme } from '../../utils/tone-color';
import { EsDatePipe } from '../../shared/es-date.pipe';

// Bug real corregido: faltaba 'gratis' -- un negocio en el plan gratis veía
// el código interno crudo ("gratis") en vez de su nombre real, por el
// fallback `?? plan` de planName() más abajo.
const PLAN_NAMES: Record<string, string> = {
  gratis:   'Plan Gratis',
  basico:   'Plan Básico',
  estandar: 'Plan Estándar',
  premium:  'Plan Premium',
};

type InvalidPaymentField = 'cardNumber' | 'expiry' | 'cvc' | 'cardholderName';

const STATUS_LABELS: Record<string, string> = {
  active:    'Activa',
  pending:   'Pendiente',
  inactive:  'Inactiva',
  cancelled: 'Cancelada',
  expired:   'Expirada',
  trial:     'Prueba gratuita',
};

@Component({
  selector: 'app-suscripcion',
  standalone: true,
  imports: [EsDatePipe, CommonModule, FormsModule, ManagementRecordCardComponent, CooldownBubbleDirective, PaginationComponent, CardPaymentFormComponent],
  template: `
    <div class="suscripcion-container">
      <h2 class="section-title">Mi Suscripción</h2>

      <div *ngIf="businessContextMode" class="context-billing-notice" role="note">
        <i class="bi bi-shield-check" aria-hidden="true"></i>
        <div>
          <strong>Vista del propietario</strong>
          <span>Así ve el negocio su suscripción. Los pagos, renovaciones y cambios de plan se gestionan desde Super Admin › Suscripciones (personalizar, confirmar pagos o cambiar la fecha de renovación). Cancelar sí está disponible aquí.</span>
        </div>
      </div>

      <!-- Loading -->
      <div *ngIf="isLoading()" class="loading-state">
        <div class="spinner"></div>
        <p>Cargando información de suscripción...</p>
      </div>

      <!-- Error -->
      <div *ngIf="errorMsg()" class="error-banner">
        {{ errorMsg() }}
      </div>

      <!-- Sin suscripción -->
      <div *ngIf="!isLoading() && !subscription() && !errorMsg()" class="empty-state">
        <p>No tienes una suscripción activa.</p>
      </div>

      <!-- Tarjeta de suscripción -->
      <div *ngIf="subscription() as sub" class="subscription-card">
        <div class="sub-header">
          <div>
            <h3 class="plan-name">{{ isTrialAccount(sub) ? 'Cuenta de prueba' : getPlanName(sub.plan) }}</h3>
            <span class="status-badge" [class]="'status-' + sub.status">
              {{ sub.status === 'expired' && isTrialAccount(sub) ? 'Prueba finalizada' : getStatusLabel(sub.status) }}
            </span>
          </div>
        </div>

        <!-- Cuenta de prueba: aviso + contratación, siempre lo primero que se ve -->
        <div class="trial-alert payment-alert" *ngIf="isTrialAccount(sub)">
          <div class="alert-icon" aria-hidden="true"><i class="bi bi-stopwatch-fill"></i></div>
          <div>
            <span class="eyebrow">CUENTA DE PRUEBA</span>
            <strong>{{ trialDaysRemainingLabel(sub) }}</strong>
            <p *ngIf="sub.status === 'trial'">Estás disfrutando los beneficios de una cuenta de prueba, con acceso completo al panel durante tu periodo gratuito. Contrata ahora para conservar el acceso sin interrupciones y ampliar el límite de espacios — no perderás nada de lo que ya configuraste.</p>
            <p *ngIf="sub.status === 'expired'">Completa tu registro para recuperar el acceso al panel. Todo lo que configuraste durante la prueba (espacios, reservas y clientes) sigue guardado y no se elimina.</p>
          </div>
        </div>
        <div class="sub-actions trial-actions" *ngIf="isTrialAccount(sub) && !businessContextMode">
          <div class="plan-selector">
            <label for="trialPlanSelect">Elige tu plan para contratar:</label>
            <select id="trialPlanSelect" class="plan-select" [value]="selectedPlanCode()" (change)="selectedPlanCode.set($any($event.target).value)">
              <option value="basico">Plan Básico</option>
              <option value="estandar">Plan Estándar</option>
              <option value="premium">Plan Premium</option>
            </select>
            <button class="btn-primary btn-sm" (click)="openTrialUpgrade(selectedPlanCode())" [disabled]="isActionLoading()">
              Contratar ahora
            </button>
          </div>
        </div>

        <section class="custom-agreement" *ngIf="hasCustomization(sub)">
          <div class="custom-agreement-head">
            <span class="custom-icon"><i class="bi bi-sliders2"></i></span>
            <div><span class="eyebrow">ACUERDO PERSONALIZADO</span><h4>Configuración exclusiva para tu negocio</h4><p>Estos valores sustituyen la capacidad y el precio mensual base de tu plan mientras el acuerdo permanezca activo.</p></div>
          </div>
          <div class="custom-agreement-grid">
            <article><small>Capacidad asignada</small><strong>{{ customizedSpacesLabel(sub) }}</strong><span>Espacios que puedes publicar</span></article>
            <article><small>Precio mensual acordado</small><strong>{{ effectiveMonthlyPrice(sub) | currency:'USD':'symbol':'1.2-2' }}</strong><span>Aplicable en tu próximo pago</span></article>
            <article *ngIf="sub.renewal_at"><small>Próxima fecha de pago</small><strong>{{ formatDateWithDay(sub.renewal_at) }}</strong><span>Tu ciclo contractual se conserva</span></article>
          </div>
          <div class="custom-conditions" *ngIf="sub.custom_pricing_notes"><i class="bi bi-info-circle"></i><div><small>Condiciones del acuerdo</small><p>{{ sub.custom_pricing_notes }}</p></div></div>
        </section>

        <!-- Cuenta de prueba: beneficios genéricos, nunca el nombre del plan pagado -->
        <div class="plan-benefits trial-benefits" *ngIf="isTrialAccount(sub)">
          <span class="eyebrow">TU CUENTA DE PRUEBA INCLUYE</span>
          <p>Acceso completo al panel administrativo para que explores TuSala sin compromiso.</p>
          <div class="benefit-chips">
            <span><i class="bi bi-check-lg" aria-hidden="true"></i> Gestión de espacios y reservas</span>
            <span><i class="bi bi-check-lg" aria-hidden="true"></i> Calendario y clientes</span>
            <span><i class="bi bi-check-lg" aria-hidden="true"></i> Estadísticas y reportes</span>
            <span><i class="bi bi-check-lg" aria-hidden="true"></i> Soporte durante tu prueba</span>
          </div>
          <small>{{ trialSpaceLimitLabel() }}</small>
        </div>

        <div class="plan-benefits" *ngIf="!isTrialAccount(sub) && currentPlan() as plan">
          <span class="eyebrow">TU PLAN INCLUYE</span>
          <p>{{ plan.description || 'Beneficios configurados para tu negocio.' }}</p>
          <div class="benefit-chips"><span *ngFor="let benefit of enabledBenefits(plan)"><i class="bi bi-check-lg" aria-hidden="true"></i> {{ benefit }}</span></div>
          <small>{{ spacesLimitLabel(plan) }}</small>
        </div>

        <div class="sub-details">
          <div class="detail-row">
            <span class="label">Inicio de la suscripción</span>
            <span class="value">{{ formatDateWithDay(sub.starts_at) }}</span>
          </div>
          <div class="detail-row" *ngIf="sub.renewal_at">
            <span class="label">Próximo pago</span>
            <span class="value">{{ formatDateWithDay(sub.renewal_at) }}</span>
          </div>
          <div class="detail-row" *ngIf="isTrialAccount(sub) && sub.trial_ends_at">
            <span class="label">Tu prueba vence</span>
            <span class="value">{{ formatDateWithDay(sub.trial_ends_at) }}</span>
          </div>
          <div class="detail-row" *ngIf="accessEndsAt(sub) as accessEnd">
            <span class="label">Acceso disponible hasta</span>
            <span class="value">{{ formatDateWithDay(accessEnd) }}</span>
          </div>
          <div class="detail-row" *ngIf="sub.cancelled_at">
            <span class="label">Cancelada el</span>
            <span class="value">{{ formatDateWithDay(sub.cancelled_at) }}</span>
          </div>
        </div>

        <!-- Acciones -->
        <div class="sub-actions" *ngIf="sub.status === 'active' || sub.status === 'pending' || sub.status === 'expired' || sub.status === 'inactive'">
          <div class="plan-selector" *ngIf="!businessContextMode">
            <label for="planSelect">Cambiar plan:</label>
            <select id="planSelect" class="plan-select" [value]="selectedPlanCode()" (change)="selectedPlanCode.set($any($event.target).value)">
              <option value="basico" [selected]="sub.plan === 'basico'">Plan Básico</option>
              <option value="estandar" [selected]="sub.plan === 'estandar'">Plan Estándar</option>
              <option value="premium" [selected]="sub.plan === 'premium'">Plan Premium</option>
            </select>
            <button class="btn-primary btn-sm" (click)="openPlanChange(selectedPlanCode())"
              [disabled]="selectedPlanCode() === sub.plan || isActionLoading()" cooldownBubble data-cooldown-key="suscripcion-plan-change">
              {{ isActionLoading() ? 'Procesando...' : 'Cambiar plan' }}
            </button>
            <div class="selected-plan-preview" *ngIf="selectedPlanPreview(sub.plan) as nextPlan">
              <div><span class="eyebrow">NUEVO PLAN SELECCIONADO</span><strong>{{ nextPlan.name }}</strong><small>\${{ nextPlan.price_monthly }} / mes · {{ spacesLimitLabel(nextPlan, false) }}</small></div>
              <div class="benefit-chips"><span *ngFor="let benefit of enabledBenefits(nextPlan)"><i class="bi bi-check-lg" aria-hidden="true"></i> {{ benefit }}</span></div>
            </div>
          </div>

          <div class="cancel-block">
            <button class="btn-danger btn-sm" (click)="openCancellation()">
              Cancelar suscripción
            </button>
          </div>
        </div>

      </div>

      <div class="cancel-modal-overlay" *ngIf="cancelModalStep() > 0" (click)="closeCancellation()" role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
        <section class="cancel-modal" [class.final-warning]="cancelModalStep() >= 2" (click)="$event.stopPropagation()">
          <button type="button" class="cancel-modal-close" (click)="closeCancellation()" [disabled]="isActionLoading()" aria-label="Cerrar"><i class="bi bi-x-lg"></i></button>

          <ng-container *ngIf="cancelModalStep() === 1">
            <div class="warning-symbol first-warning" aria-hidden="true"><i class="bi bi-exclamation-triangle-fill"></i></div>
            <span class="cancel-eyebrow">REVISA ANTES DE CONTINUAR</span>
            <h3 id="cancel-modal-title">¿Deseas cancelar tu suscripción?</h3>
            <p class="cancel-intro">No se realizará ningún cobro adicional. Podrás seguir utilizando TuSala hasta que termine el periodo que ya pagaste.</p>
            <div class="access-deadline">
              <span>Acceso disponible hasta</span>
              <strong>{{ formatDateWithDay(subscription()?.renewal_at) }}</strong>
            </div>
            <ul class="cancel-facts">
              <li><i class="bi bi-check-circle"></i><span>Tus espacios, reservas y configuraciones no se eliminan al confirmar esta solicitud.</span></li>
              <li><i class="bi bi-clock-history"></i><span>La cancelación quedará programada para el final del periodo vigente.</span></li>
              <li><i class="bi bi-credit-card"></i><span>No se renovará automáticamente ni se generará un nuevo cobro.</span></li>
            </ul>
            <div class="cancel-modal-actions">
              <button type="button" class="cancel-keep" (click)="closeCancellation()">Mantener mi suscripción</button>
              <button type="button" class="cancel-continue" (click)="cancelModalStep.set(2)">Sí, continuar</button>
            </div>
          </ng-container>

          <ng-container *ngIf="cancelModalStep() === 2">
            <div class="warning-symbol final-symbol" aria-hidden="true"><i class="bi bi-shield-exclamation"></i></div>
            <span class="cancel-eyebrow danger">CONFIRMACIÓN DEFINITIVA</span>
            <h3 id="cancel-modal-title">Esta decisión limitará tu negocio al finalizar el periodo</h3>
            <p class="cancel-intro">Después del <strong>{{ formatDateWithDay(subscription()?.renewal_at) }}</strong> perderás el acceso administrativo y necesitarás contactar a soporte para solicitar una reactivación.</p>
            <div class="loss-panel">
              <strong>Dejarás de utilizar:</strong>
              <div class="loss-grid">
                <span><i class="bi bi-building"></i> Gestión de espacios</span>
                <span><i class="bi bi-calendar2-check"></i> Reservas y calendario</span>
                <span><i class="bi bi-people"></i> Clientes y equipo</span>
                <span><i class="bi bi-graph-up"></i> Estadísticas y reportes</span>
                <span *ngFor="let benefit of enabledBenefits(currentPlan())"><i class="bi bi-x-circle"></i> {{ benefit }}</span>
              </div>
            </div>
            <div class="support-notice" *ngIf="!businessContextMode"><i class="bi bi-headset"></i><span><strong>Reactivación protegida</strong>Solo soporte o la administración de TuSala podrá habilitar nuevamente la suscripción después de que finalice el acceso.</span></div>
            <div class="cancel-modal-actions">
              <button type="button" class="cancel-keep" (click)="closeCancellation()">Conservar mi suscripción</button>
              <button type="button" class="cancel-definitive" (click)="openCancellationVerification()">Cancelar definitivamente</button>
            </div>
          </ng-container>

          <ng-container *ngIf="cancelModalStep() === 3">
            <div class="warning-symbol identity-symbol" aria-hidden="true"><i class="bi bi-lock-fill"></i></div>
            <span class="cancel-eyebrow danger">VALIDACIÓN DEL PROPIETARIO</span>
            <h3 id="cancel-modal-title">Confirma tu identidad y registra el motivo</h3>
            <p class="cancel-intro">Esta verificación protege tu negocio contra cancelaciones accidentales o realizadas por otra persona.</p>
            <div class="cancellation-form">
              <ng-container *ngIf="!businessContextMode">
              <label for="cancellation-password">Contraseña actual</label>
              <div class="password-confirmation">
                <input id="cancellation-password" [type]="showCancellationPassword() ? 'text' : 'password'"
                  [(ngModel)]="cancellationPassword" autocomplete="current-password"
                  maxlength="200" placeholder="Ingresa tu contraseña de inicio de sesión">
                <button type="button" (click)="showCancellationPassword.set(!showCancellationPassword())"
                  [attr.aria-label]="showCancellationPassword() ? 'Ocultar contraseña' : 'Mostrar contraseña'">
                  <i class="bi" [class.bi-eye]="!showCancellationPassword()" [class.bi-eye-slash]="showCancellationPassword()"></i>
                </button>
              </div>
              </ng-container>
              <label for="cancellation-reason">Motivo de la cancelación</label>
              <textarea id="cancellation-reason" [(ngModel)]="cancellationReason" maxlength="500" rows="4"
                placeholder="Cuéntanos por qué deseas cancelar (mínimo 10 caracteres)"></textarea>
              <small class="reason-counter">{{ cancellationReason.trim().length }} / 500 caracteres</small>
              <div *ngIf="cancellationError()" class="cancellation-error" role="alert">
                <i class="bi bi-exclamation-octagon-fill"></i><span>{{ cancellationError() }}</span>
              </div>
            </div>
            <div class="support-notice"><i class="bi bi-headset"></i><span><strong>¿No recuerdas tu contraseña?</strong>No intentes repetidamente. Conserva tu suscripción y contacta a soporte de TuSala para recibir ayuda.</span></div>
            <div class="cancel-modal-actions">
              <button type="button" class="cancel-keep" (click)="closeCancellation()" [disabled]="isActionLoading()">Conservar mi suscripción</button>
              <button type="button" class="cancel-definitive" (click)="cancelSubscription()" [disabled]="isActionLoading()">
                <span *ngIf="isActionLoading()" class="button-spinner"></span>
                {{ isActionLoading() ? 'Verificando…' : 'Confirmar cancelación' }}
              </button>
            </div>
          </ng-container>
        </section>
      </div>

      <div *ngIf="actionMsg()" class="action-message result-banner" [class.success]="actionSuccess()" role="status">
        <i class="bi" [class.bi-check-circle-fill]="actionSuccess()" [class.bi-exclamation-circle-fill]="!actionSuccess()"></i>
        <div><strong>{{ actionSuccess() ? 'Operación completada' : 'No se pudo completar la operación' }}</strong><span>{{ actionMsg() }}</span></div>
        <button type="button" (click)="actionMsg.set('')" aria-label="Cerrar mensaje">×</button>
      </div>

      <section class="advance-renewal" *ngIf="!businessContextMode && (subscription()?.status === 'active' || subscription()?.status === 'expired' || subscription()?.status === 'inactive') && currentPlan() as plan">
        <div *ngIf="subscription()?.status === 'expired' || subscription()?.status === 'inactive'" class="renewal-alert payment-alert">
          <div class="alert-icon" aria-hidden="true"><i class="bi bi-exclamation-octagon-fill"></i></div>
          <div><span class="eyebrow">SUSCRIPCIÓN VENCIDA</span><strong>Reactiva tu cuenta para continuar</strong><p>Realiza el pago de renovación. Tus datos y configuraciones se conservarán y el nuevo periodo comenzará al confirmarse el cobro.</p></div>
        </div>
        <div *ngIf="isRenewalWindow()" class="renewal-alert payment-alert">
          <div class="alert-icon" aria-hidden="true"><i class="bi bi-bell-fill"></i></div>
          <div>
            <span class="eyebrow">PAGO PRÓXIMO</span>
            <strong>{{ renewalAlertTitle() }}</strong>
            <p>Tu periodo actual finaliza el {{ formatDateWithDay(subscription()?.renewal_at) }}. Paga la renovación para recuperar o mantener el servicio; tu día contractual no cambiará.</p>
          </div>
        </div>
        <div class="renewal-copy">
          <span class="eyebrow">RENOVACIÓN ANTICIPADA</span>
          <h3>Planifica tus próximos meses</h3>
          <p>Tu ciclo conserva su fecha original cuando aún tienes tiempo vigente. El periodo pagado se agregará después del
            <strong>{{ formatDateWithDay(subscription()?.renewal_at) }}</strong>, aunque realices el pago hoy.</p>
        </div>
        <div class="months-input-block">
          <label for="advanceMonths">¿Cuántos meses deseas pagar?</label>
          <div class="months-input-shell">
            <input placeholder="Ej: 3" id="advanceMonths" type="number" min="1" max="60" step="1"
              [(ngModel)]="advanceMonths" (ngModelChange)="normalizeMonthsInput()"
              (keydown)="preventInvalidMonthsKey($event)" (paste)="sanitizeMonthsPaste($event)"
              inputmode="numeric" aria-describedby="monthsHelp">
            <span>{{ advanceMonths === 1 ? 'mes' : 'meses' }}</span>
          </div>
          <small id="monthsHelp">Puedes anticipar de 1 a 60 meses. El cálculo se actualiza automáticamente.</small>
        </div>
        <div class="renewal-summary">
          <div><span>Periodo adquirido</span><strong>{{ advanceMonths }} {{ advanceMonths === 1 ? 'mes' : 'meses' }}</strong></div>
          <div><span>Próximo pago después del anticipo</span><strong>{{ formatDateWithDay(projectedRenewalDate()) }}</strong></div>
          <div><span>Total</span><strong>\${{ renewalTotal() | number:'1.2-2' }}</strong></div>
          <button class="btn-primary" (click)="openRenewalPayment()" [disabled]="isActionLoading()" cooldownBubble data-cooldown-key="suscripcion-renewal">Pagar renovación</button>
        </div>
      </section>

      <div *ngIf="showPlanChange()" class="modal-overlay">
        <section class="payment-modal" role="dialog" aria-modal="true">
          <button *ngIf="canClosePaymentModal()" class="close-modal" (click)="closePaymentModal()">×</button>

          <!-- Conversión de prueba, paso 1: datos de la cuenta -->
          <ng-container *ngIf="paymentPurpose() === 'trial_upgrade' && trialConversionStage() === 'profile'">
            <span class="eyebrow">PASO 1 DE 3 · TUS DATOS</span>
            <h3>Confirma los datos de tu cuenta</h3>
            <p>Puedes actualizar tu correo si lo necesitas; validamos que no esté en uso por otra cuenta.</p>
            <div *ngIf="trialProfileLoading() && !trialProfileLoaded" class="processing-message">Cargando tus datos…</div>
            <ng-container *ngIf="trialProfileLoaded">
              <label>Nombre completo<input placeholder="Ej: Ana Pérez" [(ngModel)]="trialProfileForm.name" maxlength="100"></label>
              <label>Empresa / Negocio<input placeholder="Ej: Centro de Negocios Cumbre" [(ngModel)]="trialProfileForm.company" maxlength="100"></label>
              <label>Correo electrónico<input placeholder="nombre@correo.com" type="email" [(ngModel)]="trialProfileForm.email" maxlength="150"></label>
              <label>Teléfono<input placeholder="Ej: +503 7000-0000" type="tel" [(ngModel)]="trialProfileForm.phone" maxlength="20"></label>
            </ng-container>
            <div *ngIf="trialProfileError()" class="payment-form-error"><i class="bi bi-exclamation-circle"></i>{{ trialProfileError() }}</div>
            <button class="btn-primary payment-btn" [disabled]="trialProfileLoading() || !trialProfileLoaded" (click)="submitTrialProfile()">
              {{ trialProfileLoading() ? 'Guardando…' : 'Continuar' }}
            </button>
          </ng-container>

          <!-- Conversión de prueba, paso 2: verificación de correo -->
          <ng-container *ngIf="paymentPurpose() === 'trial_upgrade' && trialConversionStage() === 'otp'">
            <span class="eyebrow">PASO 2 DE 3 · VERIFICA TU CORREO</span>
            <h3>Ingresa el código que enviamos</h3>
            <p>Enviamos un código de 6 dígitos a <strong>{{ trialProfileForm.email }}</strong>.</p>
            <label>Código de verificación<input type="text" inputmode="numeric" maxlength="6" [ngModel]="trialOtpCode" (input)="sanitizeTrialOtpInput($event)" class="otp-code-input" placeholder="000000"></label>
            <div *ngIf="trialProfileError()" class="payment-form-error"><i class="bi bi-exclamation-circle"></i>{{ trialProfileError() }}</div>
            <button class="btn-primary payment-btn" [disabled]="trialProfileLoading() || trialOtpCode.length !== 6" (click)="verifyTrialOtp()">
              {{ trialProfileLoading() ? 'Verificando…' : 'Verificar y continuar' }}
            </button>
            <div class="otp-secondary-actions">
              <button type="button" class="link-btn" [disabled]="trialProfileLoading()" (click)="sendTrialOtp()">Reenviar código</button>
              <button type="button" class="link-btn" [disabled]="trialProfileLoading()" (click)="backToTrialProfile()">Corregir mis datos</button>
            </div>
          </ng-container>

          <ng-container *ngIf="paymentStage() === 'form' && showCardFormStep()">
            <span class="eyebrow">{{ paymentPurpose() === 'renewal' ? 'RENOVACIÓN' : (paymentPurpose() === 'trial_upgrade' ? 'PASO 3 DE 3 · ELIGE TU PLAN' : 'CAMBIO DE PLAN') }}</span>
            <h3>{{ paymentPurpose() === 'renewal' ? 'Renueva tu suscripción' : selectedPlan()?.name }}</h3>
            <p>{{ paymentPurpose() === 'trial_upgrade' ? 'Termina tu prueba y activa tu cuenta definitiva. Todo lo que configuraste se conserva.' : (paymentPurpose() === 'plan_change' ? 'El nuevo plan se activa al confirmar el pago. El tiempo que aún tengas disponible se conserva y se suma al nuevo periodo.' : (selectedPlan()?.description || 'Revisa los beneficios antes de confirmar.')) }}</p>
            <div class="benefit-chips"><span *ngFor="let benefit of enabledBenefits(selectedPlan())"><i class="bi bi-check-lg" aria-hidden="true"></i> {{ benefit }}</span></div>
            <strong>Total: \${{ paymentTotal() | number:'1.2-2' }}{{ paymentPurpose() === 'renewal' ? ' por ' + advanceMonths + (advanceMonths === 1 ? ' mes' : ' meses') : '' }}</strong>
            <!-- Mismo formulario de tarjeta que las pantallas públicas (ver shared/card-payment-form). -->
            <app-card-payment-form class="payment-card-form"
              idPrefix="sub"
              [cardNumber]="cardNumber"
              [cardholderName]="cardholderName"
              [expireDate]="cardExpiry"
              [cvc]="cardCvv"
              [invalidField]="invalidField()"
              (cardChange)="onCardChange($event)"
              (fieldEdited)="clearFieldError($event)">
            </app-card-payment-form>
            <div *ngIf="paymentFormError()" class="payment-form-error"><i class="bi bi-exclamation-circle"></i>{{ paymentFormError() }}</div>
            <button class="btn-primary payment-btn" (click)="paySubscriptionPayment()" cooldownBubble data-cooldown-key="suscripcion-pay">Continuar con APay</button>
            <small class="apay-secure"><i class="bi bi-shield-lock-fill"></i> Pago seguro procesado por APay.</small>
          </ng-container>

          <ng-container *ngIf="showGatewayPanel()">
            <div class="processing-head">
              <span class="eyebrow">{{ paymentStage() === 'success' ? 'PAGO CONFIRMADO' : 'PASARELA APAY' }}</span>
              <h3>{{ paymentStage() === 'success' ? paymentResultTitle() : 'Procesando tu pago' }}</h3>
              <p>{{ paymentStage() === 'success' ? paymentStatusMessage() : 'No cierres esta ventana mientras confirmamos la transacción.' }}</p>
            </div>
            <div class="processing-orbit" *ngIf="paymentStage() !== 'success'"><span></span><i class="bi bi-credit-card-2-front"></i></div>
            <div class="payment-progress"><span [style.width.%]="paymentProgress()"></span></div>
            <strong class="processing-message" *ngIf="paymentStage() !== 'success'">{{ paymentStatusMessage() }}</strong>
            <div class="payment-steps">
              <div [class.active]="paymentProgress() >= 15" [class.done]="paymentProgress() >= 45"><b>1</b><span>Validación</span></div>
              <i></i>
              <div [class.active]="paymentProgress() >= 45" [class.done]="paymentProgress() >= 75"><b>2</b><span>APay</span></div>
              <i></i>
              <div [class.active]="paymentProgress() >= 75" [class.done]="paymentStage() === 'success'"><b>3</b><span>Confirmación</span></div>
            </div>
            <div *ngIf="safePaymentUrl()" class="gateway-frame">
              <iframe [src]="safePaymentUrl()" title="Pasarela segura APay" sandbox="allow-forms allow-scripts allow-same-origin"></iframe>
            </div>
            <div class="bank-hint" *ngIf="paymentStage() !== 'success'"><i class="bi bi-bank"></i><span>Tu banco puede solicitar una validación adicional. Espera la confirmación de TuSala antes de salir.</span></div>
            <button *ngIf="paymentStage() === 'success'" class="btn-primary payment-btn" (click)="closePaymentModal()">Cerrar</button>
          </ng-container>

          <ng-container *ngIf="paymentStage() === 'error'">
            <div class="payment-result error">
              <div class="result-icon"><i class="bi bi-x-lg"></i></div>
              <span class="eyebrow">PAGO NO COMPLETADO</span>
              <h3>Revisa la información e inténtalo nuevamente</h3>
              <p>{{ paymentStatusMessage() }}</p>
              <button class="btn-primary payment-btn" (click)="paymentStage.set('form')">Volver al formulario</button>
            </div>
          </ng-container>
        </section>
      </div>

      <!-- Historial de pagos -->
      <div class="payments-section" *ngIf="!isLoading()">
        <div class="history-heading"><div><span class="eyebrow">MOVIMIENTOS</span><h3 class="subsection-title">Historial de pagos</h3></div><span>{{ paymentsTotal() }} registros</span></div>
        <div class="payment-empty" *ngIf="payments().length === 0">Todavía no hay pagos registrados para esta suscripción.</div>
        <div class="payments-directory" *ngIf="payments().length > 0">
          <app-management-record-card
            *ngFor="let p of payments()"
            [clickable]="false"
            [tone]="p.status === 'failed' ? 'inactive' : 'default'"
            kind="finance"
            [status]="p.status === 'succeeded' ? 'success' : p.status === 'failed' ? 'danger' : p.status === 'refunded' ? 'info' : 'warning'"
            [columns]="'48px minmax(220px,1.5fr) minmax(220px,1.5fr) minmax(110px,.7fr) auto'"
          >
            <span recordIcon [style.color]="paymentStatusColor(p.status)" [style.background]="paymentStatusColorSoft(p.status)"><i class="bi bi-receipt"></i></span>
            <div recordPrimary>
              <small>PAGO</small>
              <strong class="clamp-1">{{ getPaymentConcept(p) }}</strong>
              <span class="clamp-1">{{ (p.payment_date || p.created_at) | esDate:'dd/MM/yyyy h:mm a':'-0600' }}</span>
              <span class="txn-id clamp-1" [title]="p.transaction_id">{{ p.transaction_id }}</span>
            </div>
            <div recordSummary>
              <small>PERIODO CUBIERTO</small>
              <strong class="clamp-2">{{ paymentCoverageLabel(p) }}</strong>
              <span>Monto: \${{ p.amount | number:'1.2-2' }}</span>
            </div>
            <div recordStatus>
              <small>ESTADO</small>
              <span class="status-badge" [class]="'status-' + p.status">{{ getPaymentStatusLabel(p.status) }}</span>
            </div>
            <div recordActions>
              <button *ngIf="canDownloadReceipt(p)" type="button" class="receipt-btn" (click)="downloadReceipt(p)" title="Descargar comprobante PDF"><i class="bi bi-file-earmark-pdf"></i> Comprobante</button>
              <span *ngIf="!canDownloadReceipt(p)" class="receipt-unavailable">Disponible al confirmar pago</span>
            </div>
          </app-management-record-card>
        </div>
        <app-pagination [page]="paymentsPage()" [pages]="paymentsPages()" [total]="paymentsTotal()" [limit]="paymentsLimit()" [disabled]="isLoading()" itemLabel="pagos" ariaLabel="Paginación del historial de pagos" (pageChange)="goToPaymentsPage($event)"></app-pagination>
      </div>
    </div>
  `,
  styles: [`
    .context-billing-notice{display:flex;align-items:flex-start;gap:.75rem;margin:0 0 1rem;padding:.85rem 1rem;color:var(--ts-text);border:1px solid color-mix(in srgb,var(--ts-warning) 38%,transparent);border-left:4px solid var(--ts-warning);border-radius:12px;background:color-mix(in srgb,var(--ts-warning) 10%,var(--ts-surface))}
    .context-billing-notice>i{flex:0 0 auto;margin-top:.1rem;color:var(--ts-warning);font-size:1.05rem}
    .context-billing-notice strong{display:block;font-size:.84rem}
    .context-billing-notice span{display:block;margin-top:.15rem;color:var(--ts-text-muted);font-size:.76rem;line-height:1.5}
    .suscripcion-container { padding: 1.5rem; max-width: 700px; }
    .section-title { font-size: 1.4rem; font-weight: 700; margin-bottom: 1.5rem; color: var(--text-primary, #1e293b); }
    .subsection-title { font-size: 1.1rem; font-weight: 600; margin: 1.5rem 0 0.75rem; color: var(--text-primary, #1e293b); }
    .loading-state { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; padding: 2rem; color: var(--ts-text-blue-550-3); }
    .spinner { width: 32px; height: 32px; border: 3px solid var(--ts-border-sky-100-14); border-top-color: var(--ts-border-blue-400-4); border-radius: 50%; animation: spin 0.8s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .error-banner { background: var(--ts-bg-red-50-8); color: var(--ts-text-red-650); border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 1rem; }
    .empty-state { text-align: center; color: var(--ts-text-blue-550-3); padding: 2rem; }
    .subscription-card { background: var(--ts-text); border: 1px solid var(--ts-border-sky-100-14); border-radius: 12px; padding: 1.5rem; box-shadow: 0 1px 3px var(--ts-shadow-gray-1000-a6); }
    .sub-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.25rem; }
    .plan-name { font-size: 1.25rem; font-weight: 700; margin: 0 0 0.4rem; }
    .status-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; }
    .status-active    { background: var(--ts-bg-green-50-3); color: var(--ts-text-green-750); }
    .status-pending   { background: var(--ts-bg-amber-100-3); color: var(--ts-text-orange-700); }
    .status-inactive  { background: var(--ts-bg-sky-50-12); color: var(--ts-text-blue-550-3); }
    .status-cancelled { background: var(--ts-bg-red-50-8); color: var(--ts-text-red-650); }
    .status-expired   { background: var(--ts-bg-red-50); color: var(--ts-text-red-700-2); }
    .status-completed { background: var(--ts-bg-green-50-3); color: var(--ts-text-green-750); }
    .sub-details { border-top: 1px solid var(--ts-border-sky-50-9); padding-top: 1rem; margin-bottom: 1rem; }
    .detail-row { display: flex; justify-content: space-between; padding: 0.35rem 0; font-size: 0.9rem; }
    .label { color: var(--ts-text-muted); }
    .value { font-weight: 500; }
    .sub-actions { border-top: 1px solid var(--ts-border-sky-50-9); padding-top: 1rem; display: flex; flex-wrap: wrap; gap: 1rem; align-items: flex-start; }
    .plan-selector { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; font-size: 0.88rem; }
    .plan-select { padding: 6px 10px; border: 1px solid var(--ts-border-sky-150-6); border-radius: 6px; font-size: 0.88rem; }
    .cancel-block { margin-left: auto; }
    .btn-primary  { background: var(--ts-bg-blue-400); color:var(--ts-on-primary); border: none; border-radius: 6px; padding: 7px 16px; cursor: pointer; font-size: 0.88rem; }
    .btn-primary:hover { background: var(--ts-bg-blue-450); }
    .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn-danger   { background: var(--ts-bg-red-500); color:var(--ts-on-primary); border: none; border-radius: 6px; padding: 7px 16px; cursor: pointer; font-size: 0.88rem; }
    .btn-danger:hover { background: var(--ts-bg-red-600); }
    .btn-secondary { background: var(--ts-bg-sky-100-4); color: var(--ts-text-navy-850); border: none; border-radius: 6px; padding: 7px 16px; cursor: pointer; font-size: 0.88rem; }
    .btn-sm { padding: 5px 12px; font-size: 0.82rem; }
    .action-message { margin-top: 1rem; padding: 0.6rem 1rem; border-radius: 6px; background: var(--ts-bg-red-50-8); color: var(--ts-text-red-650); font-size: 0.88rem; }
    .action-message.success { background: var(--ts-bg-green-50-3); color: var(--ts-text-green-750); }
    .payments-directory { display: grid; gap: .7rem; }
    .txn-id { font-family: monospace; font-size: 0.78rem; color: var(--ts-text-blue-550-3); }
    .receipt-btn { display:inline-flex;align-items:center;gap:.35rem;padding:.38rem .62rem;border:1px solid var(--ts-border-blue-300-a35);border-radius:8px;background:var(--ts-bg-blue-400-a10);color:var(--ts-text-blue-150-10);font-weight:750;cursor:pointer; }
    .receipt-btn:hover { border-color:var(--ts-border-blue-300-3);background:var(--ts-bg-blue-400-a18-2); }
    .receipt-unavailable { color:var(--ts-text-muted,#7487a0);font-size:.75rem; }
    .suscripcion-container { width: 100%; max-width: none; padding: 1.75rem; color: var(--ts-text-navy-750-9); box-sizing: border-box; }
    .section-title { margin: 0 0 1.15rem; color: var(--ts-text-navy-800-2); font-size: 1.75rem; letter-spacing: -.045em; }
    .subscription-card { overflow: hidden; padding: 0; border: 1px solid var(--ts-border-blue-100-6); border-radius: 18px; box-shadow: 0 12px 32px var(--ts-shadow-navy-800-a8); }
    .sub-header { margin: 0; padding: 1.45rem 1.5rem; color: var(--ts-text); background: linear-gradient(120deg, var(--ts-bg-navy-750-8), var(--ts-bg-sky-500-5)); } .plan-name { color: var(--ts-text); font-size: 1.5rem; letter-spacing: -.03em; } .status-badge { border: 1px solid var(--ts-border-gray-0-a30); background: var(--ts-bg-gray-0-a15); color: var(--ts-text); }
    .plan-benefits { margin: 1.25rem 1.5rem; padding: 1rem 1.1rem; border: 1px solid var(--ts-border-blue-100-7); border-radius: 12px; background: linear-gradient(135deg, var(--ts-bg-blue-50-8), var(--ts-bg-sky-0-3)); } .plan-benefits p { margin: .25rem 0 .65rem; color: var(--ts-text-blue-550-13); font-size: .88rem; } .plan-benefits small { color: var(--ts-text-sky-600-6); font-weight: 700; font-size: .78rem; }
    .eyebrow { display: block; color: var(--ts-text-blue-500-19); font-size: .66rem; font-weight: 800; letter-spacing: .11em; } .benefit-chips { display: flex; flex-wrap: wrap; gap: .4rem; margin: .6rem 0; } .benefit-chips span { padding: .28rem .55rem; border: 1px solid var(--ts-border-blue-100-8); border-radius: 999px; background: var(--ts-text); color: var(--ts-text-blue-550-14); font-size: .75rem; font-weight: 700; }
    .sub-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: .75rem; margin: 0; padding: 0 1.5rem 1.25rem; border: 0; } .detail-row { display: grid; gap: .2rem; padding: .7rem .8rem; border: 1px solid var(--ts-border-blue-50-5); border-radius: 10px; background: var(--ts-bg-blue-0-8); } .label { font-size: .72rem; font-weight: 750; text-transform: uppercase; letter-spacing: .05em; } .value { color: var(--ts-text-navy-750-10); }
    .sub-actions { margin: 0; padding: 1.15rem 1.5rem; border-top: 1px solid var(--ts-border-blue-50-6); background: var(--ts-bg-blue-0-9); } .plan-selector { display: grid; grid-template-columns: auto minmax(180px, 300px) auto; align-items: center; gap: .65rem; flex: 1; } .plan-select { min-width: 180px; padding: .55rem .7rem; border: 1px solid var(--ts-border-blue-150-4); border-radius: 8px; background: var(--ts-text); } .btn-primary { border-radius: 8px; background: linear-gradient(135deg, var(--ts-bg-blue-500), var(--ts-bg-sky-450)); box-shadow: 0 6px 14px var(--ts-shadow-blue-500-a20-4); font-weight: 700; } .btn-danger { border-radius: 8px; }
    .selected-plan-preview { grid-column: 1 / -1; display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-top: .25rem; padding: .9rem 1rem; border: 1px solid var(--ts-border-blue-100-3); border-radius: 11px; background: var(--ts-bg-blue-50-14); } .selected-plan-preview strong { display: block; margin: .18rem 0; color: var(--ts-text-blue-650); } .selected-plan-preview small { color: var(--ts-text-blue-550-15); }
    .modal-overlay { position: fixed; inset: 0; z-index: 1200; display: grid; place-items: center; padding: 1rem; background: var(--ts-bg-navy-900-a62-2); } .payment-modal { position: relative; width: min(520px, 100%); max-height: calc(100vh - 2rem); overflow-y: auto; padding: 1.6rem; border-radius: 18px; background: var(--ts-text); box-shadow: 0 28px 80px var(--ts-shadow-gray-1000-a30); } .payment-modal h3 { margin: .25rem 0; color: var(--ts-text-navy-800-2); font-size: 1.35rem; } .payment-modal > p { color: var(--ts-text-blue-500-20); font-size: .88rem; } .payment-modal label { display: grid; gap: .35rem; margin-top: .8rem; color: var(--ts-text-navy-650-4); font-size: .78rem; font-weight: 700; } .payment-modal input { padding: .68rem .72rem; border: 1px solid var(--ts-border-blue-100-9); border-radius: 8px; font: inherit; } .payment-row { display: grid; grid-template-columns: 1fr 1fr; gap: .7rem; } .payment-card-form { display: block; margin-top: 1rem; } .payment-card-form label { margin-top: 0; } .payment-btn { width: 100%; margin: 1.2rem 0 .6rem; padding: .78rem; } .close-modal { position: absolute; top: .65rem; right: .8rem; border: 0; background: transparent; color: var(--ts-text-blue-450-2); font-size: 1.4rem; cursor: pointer; }
    .payments-section { margin-top: 1.35rem; padding: 1.2rem; border: 1px solid var(--ts-border-sky-100-11); border-radius: 15px; background: var(--ts-text); } .renewal-alert { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom:1rem; padding:1rem 1.1rem; border:1px solid var(--ts-border-amber-250); border-radius:12px; background:linear-gradient(135deg,var(--ts-bg-amber-50-2),var(--ts-bg-amber-0)); } .renewal-alert strong { display:block; margin:.2rem 0; color:var(--ts-text-orange-750); font-size:1rem; } .renewal-alert p { margin:0; color:var(--ts-text-amber-650); font-size:.82rem; line-height:1.45; } .renewal-alert .btn-primary { white-space:nowrap; }
    .trial-alert { display:flex; align-items:center; gap:1rem; margin: 0 1.5rem 1rem; padding:1rem 1.1rem; border:1px solid var(--ts-border-teal-250); border-radius:12px; background:linear-gradient(135deg,var(--ts-bg-teal-50-2),var(--ts-bg-teal-0)); } .trial-alert strong { display:block; margin:.2rem 0; color:var(--ts-text-teal-750); font-size:1rem; } .trial-alert p { margin:0; color:var(--ts-text-teal-700-2); font-size:.82rem; line-height:1.45; } .trial-alert .alert-icon { background:var(--ts-bg-teal-400-3); color:var(--ts-text-teal-900); }
    .sub-actions.trial-actions { margin: 0 1.5rem 1.15rem; padding: 0 0 1.15rem; border-top: 0; } .history-heading { display: flex; justify-content: space-between; align-items: center; margin-bottom: .85rem; } .history-heading > span { border-radius: 999px; padding: .28rem .55rem; background: var(--ts-bg-sky-50-14); color: var(--ts-text-sky-550-6); font-size: .75rem; font-weight: 700; } .history-heading .subsection-title { margin: .15rem 0 0; color: var(--ts-text-navy-750-11); } .payment-empty { padding: 1.2rem; border: 1px dashed var(--ts-border-sky-150-7); border-radius: 10px; text-align: center; color: var(--ts-text-blue-500-21); }
    .status-succeeded { background: var(--ts-bg-green-50-3); color: var(--ts-text-green-750); } .status-failed { background: var(--ts-bg-red-50-8); color: var(--ts-text-red-600-6); }
    @media (max-width: 700px) { .suscripcion-container { padding: 1rem; } .plan-selector { grid-template-columns: 1fr; } .selected-plan-preview, .renewal-alert, .trial-alert { align-items: flex-start; flex-direction: column; } .renewal-alert .btn-primary { width:100%; } .sub-header, .plan-benefits, .sub-details, .sub-actions { margin-left: 1rem; margin-right: 1rem; } .sub-header { margin: 0; } .payment-row { grid-template-columns: 1fr; } }
    /* Tema TuSala: elimina las superficies claras de la tarjeta y sus modales. */
    .subscription-card, .payments-section {
      color: var(--ts-text);
      background: linear-gradient(145deg, var(--ts-surface-2), var(--ts-surface-3));
      border-color: var(--ts-border);
      box-shadow: var(--ts-shadow-sm);
    }
    .plan-benefits {
      background: var(--ts-bg-blue-400-a5);
      border-color: var(--ts-border-blue-400-a22);
    }
    .plan-benefits p, .plan-benefits small { color: var(--ts-text-muted); }
    .benefit-chips span {
      color: var(--ts-text-blue-150-4);
      background: var(--ts-primary-soft-07);
      border-color: var(--ts-border-blue-400-a24);
    }
    .detail-row {
      color: var(--ts-text);
      background: var(--ts-wash-035);
      border-color: var(--ts-border);
    }
    .detail-row .value { color: var(--ts-text); }
    .sub-actions {
      background: var(--ts-bg-navy-950-a28);
      border-color: var(--ts-border);
    }
    .history-heading > span {
      color: var(--ts-text-blue-150-4);
      background: var(--ts-primary-soft-07);
    }
    .history-heading .subsection-title { color: var(--ts-text); }
    .payment-modal {
      color: var(--ts-text);
      background: linear-gradient(145deg, var(--ts-surface-soft), var(--ts-bg-elevated));
      border: 1px solid var(--ts-border-strong);
      box-shadow: var(--ts-shadow-lg);
    }
    .payment-modal h3 { color: var(--ts-text); }
    .payment-modal > p, .payment-modal label { color: var(--ts-text-muted); }
    .renewal-alert {
      color: var(--ts-warning-text);
      background: var(--ts-bg-orange-450-a9);
      border-color: var(--ts-border-orange-450-a28);
    }
    .renewal-alert strong, .renewal-alert p { color: inherit; }
    .trial-alert {
      color: var(--ts-text-teal-250-2);
      background: var(--ts-bg-teal-500-a9);
      border-color: var(--ts-border-teal-400-a30);
    }
    .trial-alert strong, .trial-alert p { color: inherit; }
    .sub-actions.trial-actions { background: transparent; }
    .advance-renewal { margin-top: 1.35rem; padding: 1.25rem; border: 1px solid var(--ts-border-blue-350-a35-3); border-radius: 16px; background: linear-gradient(135deg, var(--ts-bg-blue-600-a16), var(--ts-bg-indigo-400-a8)); }
    .renewal-copy h3 { margin: .2rem 0 .35rem; color: var(--ts-text, var(--ts-text)); font-size: 1.18rem; }
    .renewal-copy p { max-width: 780px; margin: 0; color: var(--ts-text-muted, #91a7c6); line-height: 1.5; }
    .months-input-block { display: grid; grid-template-columns: minmax(220px, 320px) 1fr; align-items: center; gap: .35rem 1rem; margin: 1rem 0; }
    .months-input-block label { grid-column: 1 / -1; color: var(--ts-text,#eef5ff); font-size: .78rem; font-weight: 800; }
    .months-input-block small { color: var(--ts-text-muted,#91a7c6); font-size: .76rem; }
    .months-input-shell { display: flex; align-items: stretch; overflow: hidden; border: 1px solid var(--ts-border-blue-350-a38-2); border-radius: 11px; background: var(--ts-bg-navy-900-a78); transition: border-color .18s ease, box-shadow .18s ease; }
    .months-input-shell:focus-within { border-color: var(--ts-border-blue-300-6); box-shadow: 0 0 0 3px var(--ts-shadow-blue-400-a14-2); }
    .months-input-shell input { min-width: 0; width: 100%; padding: .72rem .85rem; border: 0; outline: 0; color: var(--ts-text,#eef5ff); background: transparent; font-family: inherit; font-size: 1rem; font-weight: 800; }
    .months-input-shell span { display: grid; place-items: center; min-width: 72px; padding: 0 .7rem; border-left: 1px solid var(--ts-border-blue-350-a24); color: var(--ts-text-blue-150-10); background: var(--ts-bg-blue-400-a8); font-size: .78rem; font-weight: 800; }
    .renewal-summary { display: grid; grid-template-columns: repeat(3,minmax(140px,1fr)) auto; align-items: end; gap: .7rem; padding: .85rem; border: 1px solid var(--ts-border-blue-350-a20-2); border-radius: 12px; background: var(--ts-bg-navy-900-a55-4); }
    .renewal-summary div { display: grid; gap: .2rem; } .renewal-summary span { color: var(--ts-text-muted,#91a7c6); font-size: .7rem; text-transform: uppercase; letter-spacing: .04em; } .renewal-summary strong { color: var(--ts-text,#eef5ff); }
    .payment-alert { position: relative; justify-content: flex-start; overflow: hidden; margin-bottom: 1rem; background: linear-gradient(120deg,var(--ts-bg-orange-600-a20),var(--ts-bg-orange-450-a9)); animation: alertGlow 2.8s ease-in-out infinite; }
    .payment-alert::after { content: ''; position: absolute; inset: 0; pointer-events: none; background: linear-gradient(105deg,transparent 30%,var(--ts-bg-amber-300-a12),transparent 70%); transform: translateX(-110%); animation: alertSweep 3.5s ease-in-out infinite; }
    .alert-icon { flex: 0 0 42px; height: 42px; display: grid; place-items: center; border-radius: 50%; color: var(--ts-text-navy-900); background: var(--ts-bg-amber-350); box-shadow: 0 0 0 7px var(--ts-shadow-amber-350-a10); animation: alertBell 2.4s ease-in-out infinite; }
    .payment-alert > div:not(.alert-icon) { position: relative; z-index: 1; }
    @keyframes alertGlow { 0%,100% { border-color: var(--ts-border-orange-450-a30); box-shadow: 0 0 0 var(--ts-shadow-orange-450-a0); } 50% { border-color: var(--ts-border-amber-350-a65); box-shadow: 0 0 24px var(--ts-shadow-orange-450-a10); } }
    @keyframes alertSweep { 0%,45% { transform: translateX(-110%); } 75%,100% { transform: translateX(110%); } }
    @keyframes alertBell { 0%,88%,100% { transform: rotate(0); } 91% { transform: rotate(10deg); } 94% { transform: rotate(-10deg); } 97% { transform: rotate(5deg); } }
    .section-title { color: var(--ts-text,#eef5ff); }
    .plan-select, .payment-modal input { color: var(--ts-text,#eef5ff); background: var(--ts-bg-navy-900-a72); border-color: var(--ts-border); }
    .selected-plan-preview { color: var(--ts-text); background: var(--ts-bg-blue-400-a7); border-color: var(--ts-border-blue-400-a24); }
    .selected-plan-preview strong { color: var(--ts-text-blue-150-4); } .selected-plan-preview small { color: var(--ts-text-muted); }
    .txn-id { color: var(--ts-text-muted); }
    .action-message { border: 1px solid var(--ts-border-red-700-2); color: var(--ts-text-on-accent-27); background: var(--ts-bg-red-850-3); }
    .action-message.success { border-color: var(--ts-border-green-750) !important; color: var(--ts-text-green-100-2) !important; background: var(--ts-bg-green-850) !important; }
    .result-banner { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: .85rem; margin: 1.35rem 0; padding: 1rem 1.1rem; border-radius: 13px; box-shadow: 0 10px 28px var(--ts-shadow-gray-1000-a12); }
    .result-banner > i { font-size: 1.35rem; } .result-banner > div { display: grid; gap: .2rem; } .result-banner strong { color: inherit; font-size: .88rem; } .result-banner span { color: var(--ts-text-muted); line-height: 1.45; }
    .result-banner button { border: 0; padding: .2rem .4rem; color: inherit; background: transparent; font-size: 1.25rem; cursor: pointer; }
    .payment-modal { width: min(560px, 100%); max-height: min(92vh, 760px); overflow-y: auto; }
    .payment-modal:has(.gateway-frame) { width: min(520px, 100%); max-height: min(94vh, 760px); }
    .apay-secure { display: flex; justify-content: center; align-items: center; gap: .4rem; color: var(--ts-text-muted); }
    .payment-form-error { display: flex; align-items: center; gap: .45rem; margin-top: .8rem; padding: .7rem .8rem; border: 1px solid var(--ts-border-red-300-a28-2); border-radius: 9px; color: var(--ts-text-red-150-15); background: var(--ts-bg-red-550-a12); font-size: .78rem; }
    .input-error { border-color: var(--ts-border-red-300-3) !important; box-shadow: 0 0 0 3px var(--ts-shadow-red-550-a18); animation: subscriptionFieldShake .32s ease; }
    @keyframes subscriptionFieldShake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
    @media (prefers-reduced-motion: reduce) { .input-error { animation: none; } }
    .otp-code-input { letter-spacing: .4em; font-size: 1.3rem; font-weight: 800; text-align: center; }
    .otp-secondary-actions { display: flex; gap: 1.1rem; margin-top: .9rem; }
    .link-btn { border: 0; padding: 0; background: transparent; color: var(--ts-text-blue-300-13); font-size: .78rem; font-weight: 700; cursor: pointer; }
    .link-btn:disabled { opacity: .5; cursor: default; }
    .processing-head { text-align: center; } .processing-head h3 { margin: .3rem 0; } .processing-head p { margin: 0; color: var(--ts-text-muted); }
    .processing-orbit { position: relative; width: 88px; height: 88px; display: grid; place-items: center; margin: 1.4rem auto 1rem; border-radius: 50%; color: var(--ts-text-blue-250-30); background: var(--ts-bg-blue-400-a10); }
    .processing-orbit span { position: absolute; inset: 0; border: 3px solid var(--ts-border-blue-300-a16); border-top-color: var(--ts-border-blue-300-7); border-right-color: var(--ts-border-indigo-300); border-radius: 50%; animation: paymentSpin 1s linear infinite; }
    .processing-orbit i { font-size: 1.8rem; }
    .payment-progress { height: 8px; overflow: hidden; margin: .9rem 0 .55rem; border-radius: 999px; background: var(--ts-bg-sky-400-a16); }
    .payment-progress span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg,var(--ts-primary),var(--ts-bg-indigo-350-20)); transition: width .45s ease; }
    .processing-message { display: block; min-height: 1.4rem; text-align: center; color: var(--ts-text-blue-100-4); font-size: .88rem; }
    .payment-steps { display: grid; grid-template-columns: auto 1fr auto 1fr auto; align-items: center; gap: .5rem; margin: 1.2rem 0; }
    .payment-steps > div { display: grid; justify-items: center; gap: .35rem; color: var(--ts-text-muted); font-size: .68rem; }
    .payment-steps b { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 50%; background: var(--ts-bg-sky-400-a14); }
    .payment-steps > i { height: 2px; background: var(--ts-bg-sky-400-a18); }
    .payment-steps > div.active { color: var(--ts-text-blue-200-14); } .payment-steps > div.active b { color: var(--ts-text); background: var(--ts-bg-blue-450-10); }
    .payment-steps > div.done b { background: var(--ts-bg-teal-700); }
    .gateway-frame { overflow: hidden; width: 100%; max-width: 420px; height: 400px; margin: 1rem auto; border: 1px solid var(--ts-border-strong); border-radius: 10px; background: var(--ts-text); }
    @media (max-width: 700px) { .gateway-frame { max-width: 100%; height: 360px; } }
    .gateway-frame iframe { width: 100%; height: 100%; border: 0; }
    .bank-hint { display: flex; align-items: center; gap: .7rem; padding: .8rem; border: 1px solid var(--ts-border-orange-450-a24); border-radius: 10px; color: var(--ts-text-amber-250-4); background: var(--ts-bg-orange-700-a14); font-size: .76rem; line-height: 1.4; }
    .bank-hint i { font-size: 1.15rem; }
    .payment-result { display: grid; justify-items: center; gap: .65rem; padding: .5rem 0; text-align: center; background: transparent !important; color: inherit !important; }
    .payment-result .result-icon { width: 68px; height: 68px; display: grid; place-items: center; margin-bottom: .3rem; border-radius: 50%; font-size: 1.8rem; }
    .payment-result.success .result-icon { color: var(--ts-text-green-250-4); background: var(--ts-bg-teal-600-a16); box-shadow: 0 0 0 8px var(--ts-shadow-teal-600-a6); }
    .payment-result.error .result-icon { color: var(--ts-text-red-150-16); background: var(--ts-bg-red-550-a16); box-shadow: 0 0 0 8px var(--ts-shadow-red-550-a6); }
    .payment-result p { max-width: 430px; margin: 0; color: var(--ts-text-muted); line-height: 1.5; }
    .result-summary { width: 100%; display: flex; justify-content: space-between; align-items: center; margin-top: .6rem; padding: .85rem 1rem; border: 1px solid var(--ts-border); border-radius: 10px; background: var(--ts-bg-navy-950-a42-3); }
    .result-summary span { color: var(--ts-text-muted); font-size: .78rem; } .result-summary strong { color: var(--ts-text); }

    /* Superficie uniforme del módulo: referencia visual de Renovación anticipada. */
    .suscripcion-container {
      --subscription-surface: linear-gradient(135deg, var(--ts-bg-blue-600-a16), var(--ts-bg-indigo-400-a8));
      --subscription-inner: var(--ts-bg-navy-900-a55-4);
      --subscription-border: var(--ts-border-blue-350-a35-3);
    }
    .subscription-card,
    .advance-renewal,
    .payments-section {
      color: var(--ts-text);
      background: var(--subscription-surface);
      border-color: var(--subscription-border);
      box-shadow: none;
    }
    .sub-header {
      color: var(--ts-text);
      background: transparent;
      border-bottom: 1px solid var(--ts-border-blue-350-a20-2);
    }
    .plan-name { color: var(--ts-text); }
    .plan-benefits,
    .detail-row,
    .sub-actions,
    .selected-plan-preview,
    .payment-empty {
      color: var(--ts-text);
      background: var(--subscription-inner);
      border-color: var(--ts-border-blue-350-a20-2);
    }
    .sub-actions { border-top-color: var(--ts-border-blue-350-a20-2); }
    .history-heading > span,
    .benefit-chips span,
    .selected-plan-preview {
      background-color: var(--ts-bg-blue-400-a8);
    }
    .cancel-modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 1350;
      display: grid;
      place-items: center;
      padding: 20px;
      background: var(--ts-bg-navy-950-a78-3);
      backdrop-filter: blur(8px);
      animation: cancelBackdropIn .22s ease both;
    }
    .cancel-modal {
      position: relative;
      width: min(640px,100%);
      max-height: min(92vh,820px);
      overflow-x: hidden;
      overflow-y: auto;
      padding: 28px;
      border: 1px solid var(--ts-border-orange-450-a38);
      border-radius: 20px;
      color: var(--ts-text);
      background: radial-gradient(circle at 50% 0,var(--ts-bg-orange-450-a11),transparent 35%),linear-gradient(145deg,var(--ts-surface-soft),var(--ts-bg-navy-900-11));
      box-shadow: 0 28px 80px var(--ts-shadow-gray-1000-a46);
      animation: cancelModalIn .3s cubic-bezier(.2,.8,.25,1) both;
    }
    .cancel-modal.final-warning {
      border-color: var(--ts-border-red-350-a44);
      background: radial-gradient(circle at 50% 0,var(--ts-bg-red-350-a13),transparent 38%),linear-gradient(145deg,var(--ts-surface-soft),var(--ts-bg-navy-900-11));
    }
    .cancel-modal::before {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(110deg,transparent 30%,var(--ts-bg-amber-250-a7),transparent 70%);
      transform: translateX(-115%);
      animation: cancelWarningSweep 3.2s ease-in-out infinite;
    }
    .cancel-modal.final-warning::before { background: linear-gradient(110deg,transparent 30%,var(--ts-bg-red-250-a7),transparent 70%); }
    .cancel-modal-close {
      position: absolute;
      z-index: 2;
      top: 14px;
      right: 14px;
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border: 1px solid var(--ts-border);
      border-radius: 10px;
      color: var(--ts-text-sky-300-39);
      background: var(--ts-bg-navy-900-a72-2);
      cursor: pointer;
    }
    .cancel-modal-close:disabled { cursor: wait;opacity:.5 }
    .warning-symbol {
      position: relative;
      display: grid;
      place-items: center;
      width: 68px;
      height: 68px;
      margin: 0 auto 14px;
      border-radius: 50%;
      font-size: 1.75rem;
      animation: cancelWarningPulse 2.2s ease-in-out infinite;
    }
    .first-warning { color:var(--ts-text-amber-300-6);background:var(--ts-bg-orange-450-a14);box-shadow:0 0 0 9px var(--ts-shadow-orange-450-a6) }
    .final-symbol { color:var(--ts-text-red-200-15);background:var(--ts-bg-red-350-a15);box-shadow:0 0 0 9px var(--ts-shadow-red-350-a6-2) }
    .identity-symbol { color:var(--ts-text-red-150-5);background:var(--ts-bg-red-350-a14);box-shadow:0 0 0 9px var(--ts-shadow-red-350-a5) }
    .cancel-eyebrow { display:block;text-align:center;color:var(--ts-text-amber-350-3);font-size:.67rem;font-weight:850;letter-spacing:.13em }
    .cancel-eyebrow.danger {
      padding:0!important;
      border:0!important;
      color:var(--ts-text-red-250-10);
      background:transparent!important;
      box-shadow:none!important;
      font-size:.78rem;
      line-height:1.35;
    }
    .cancel-modal h3 { max-width:540px;margin:7px auto 9px;text-align:center;color:var(--ts-text);font-size:1.35rem;line-height:1.25 }
    .cancel-intro { max-width:560px;margin:0 auto 16px;text-align:center;color:var(--ts-text-sky-300-40);font-size:.86rem;line-height:1.55 }
    .cancel-intro strong { color:var(--ts-text) }
    .access-deadline { display:flex;align-items:center;justify-content:space-between;gap:16px;margin:15px 0;padding:13px 15px;border:1px solid var(--ts-border-orange-450-a24);border-radius:11px;color:var(--ts-text-amber-350-4);background:var(--ts-bg-orange-700-a11) }
    .access-deadline span { font-size:.78rem }.access-deadline strong { color:var(--ts-text-amber-200-3);font-size:1rem }
    .cancel-facts { display:grid;gap:8px;margin:0;padding:0;list-style:none }
    .cancel-facts li { display:grid;grid-template-columns:22px 1fr;gap:9px;align-items:start;padding:9px 11px;border:1px solid var(--ts-border-blue-350-a15);border-radius:9px;color:var(--ts-text-sky-250-36);background:var(--ts-bg-navy-900-a48);font-size:.78rem;line-height:1.45 }
    .cancel-facts i { color:var(--ts-text-teal-400-4);font-size:.9rem }
    .loss-panel { margin:15px 0;padding:14px;border:1px solid var(--ts-border-red-350-a23);border-radius:12px;background:var(--ts-bg-red-750-a10) }
    .loss-panel>strong { display:block;margin-bottom:10px;color:var(--ts-text-red-100-3);font-size:.8rem }
    .loss-grid { display:grid;grid-template-columns:1fr 1fr;gap:7px }
    .loss-grid span { display:flex;align-items:center;gap:7px;padding:8px 9px;border-radius:8px;color:var(--ts-text-sky-200-4);background:var(--ts-bg-navy-900-a52);font-size:.75rem }
    .loss-grid i { color:var(--ts-text-red-200-16) }
    .support-notice { display:grid;grid-template-columns:38px 1fr;gap:10px;align-items:center;padding:11px 12px;border:1px solid var(--ts-border-blue-350-a20-2);border-radius:10px;color:var(--ts-text-sky-250-37);background:var(--ts-bg-blue-400-a7);font-size:.76rem;line-height:1.4 }
    .support-notice>i { display:grid;place-items:center;width:36px;height:36px;border-radius:9px;color:var(--ts-text-blue-250-31);background:var(--ts-bg-blue-400-a12);font-size:1rem }
    .support-notice strong { display:block;color:var(--ts-text-sky-100-14) }
    .cancellation-form { display:grid;gap:7px;margin:15px 0;padding:14px;border:1px solid var(--ts-border-blue-350-a20-2);border-radius:12px;background:var(--ts-bg-navy-900-a58-2) }
    .cancellation-form label { margin-top:3px;color:var(--ts-text-sky-200-26);font-size:.74rem;font-weight:800 }
    .cancellation-form input,.cancellation-form textarea { box-sizing:border-box;width:100%;padding:10px 11px;border:1px solid var(--ts-border-navy-650-11);border-radius:9px;outline:0;color:var(--ts-text-on-accent-16);background:var(--ts-input-bg);font:inherit;font-size:.82rem;resize:vertical }
    .cancellation-form textarea { min-height:92px;max-height:150px;line-height:1.45 }
    .cancellation-form input:focus,.cancellation-form textarea:focus { border-color:var(--ts-border-blue-300-6);box-shadow:0 0 0 3px var(--ts-shadow-blue-400-a14-2) }
    .password-confirmation { display:grid;grid-template-columns:1fr 42px;overflow:hidden;border-radius:9px }
    .password-confirmation input { border-radius:9px 0 0 9px }
    .password-confirmation button { border:1px solid var(--ts-border-navy-650-11);border-left:0;border-radius:0 9px 9px 0;color:var(--ts-text-sky-250-38);background:var(--ts-bg-navy-850-62);cursor:pointer }
    .reason-counter { justify-self:end;color:var(--ts-text-sky-400-29);font-size:.67rem }
    .cancellation-error { display:grid;grid-template-columns:20px 1fr;gap:8px;align-items:start;margin-top:4px;padding:10px;border:1px solid var(--ts-border-red-350-a32);border-radius:9px;color:var(--ts-text-red-150-16);background:var(--ts-bg-red-650-a13);font-size:.75rem;line-height:1.4 }
    .cancellation-error i { margin-top:1px;color:var(--ts-text-red-250-11) }
    .cancel-modal-actions { position:relative;z-index:1;display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:18px }
    .cancel-modal-actions button { min-height:43px;padding:0 14px;border-radius:10px;font:inherit;font-size:.8rem;font-weight:800;cursor:pointer }
    .cancel-modal-actions button:disabled { cursor:wait;opacity:.65 }
    .cancel-keep {
      color:var(--ts-text-teal-900-2);
      border:1px solid var(--ts-border-green-400);
      background:linear-gradient(135deg,var(--ts-bg-green-450),var(--ts-bg-green-300));
      box-shadow:0 8px 22px var(--ts-shadow-teal-500-a22);
    }
    .custom-agreement{margin:20px 24px;padding:18px;border:1px solid var(--ts-border-blue-350-a38-3);border-radius:15px;background:radial-gradient(circle at 100% 0,var(--ts-bg-indigo-350-a16),transparent 38%),linear-gradient(145deg,var(--ts-bg-navy-750-a90),var(--ts-bg-navy-900-a95));box-shadow:0 14px 34px var(--ts-shadow-navy-950-a20)}.custom-agreement-head{display:flex;align-items:flex-start;gap:12px}.custom-icon{display:grid;place-items:center;flex:0 0 44px;height:44px;border-radius:13px;color:var(--ts-on-primary);background:linear-gradient(135deg,var(--ts-primary),var(--ts-bg-indigo-350-21));box-shadow:0 8px 20px var(--ts-shadow-blue-400-a28)}.custom-agreement h4{margin:3px 0 4px;color:var(--ts-text-blue-0-2);font-size:1rem}.custom-agreement-head p{margin:0;color:var(--ts-text-sky-300-41);font-size:.73rem;line-height:1.45}.custom-agreement-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:15px}.custom-agreement-grid article{display:grid;gap:4px;padding:13px;border:1px solid var(--ts-border-sky-300-a18-2);border-radius:11px;background:var(--ts-bg-navy-900-a46)}.custom-agreement-grid small,.custom-conditions small{color:var(--ts-text-sky-350-43);font-size:.64rem}.custom-agreement-grid strong{color:var(--ts-text);font-size:1.05rem}.custom-agreement-grid span{color:var(--ts-text-sky-400-30);font-size:.62rem}.custom-conditions{display:flex;gap:9px;margin-top:10px;padding:11px;color:var(--ts-text-blue-200-15);border-radius:10px;background:var(--ts-primary-soft-055)}.custom-conditions p{margin:3px 0 0;color:var(--ts-text-sky-150-27);font-size:.72rem;line-height:1.45}@media(max-width:700px){.custom-agreement-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:480px){.custom-agreement-grid{grid-template-columns:1fr}}
    .cancel-keep:hover:not(:disabled) { filter:brightness(1.06);box-shadow:0 10px 28px var(--ts-shadow-teal-500-a30);transform:translateY(-1px) }
    .cancel-continue { color:var(--ts-text-amber-950);border:1px solid var(--ts-border-amber-450-2);background:linear-gradient(135deg,var(--ts-bg-amber-400),var(--ts-bg-amber-300-2)) }
    .cancel-definitive { display:flex;align-items:center;justify-content:center;gap:8px;color:var(--ts-on-primary);border:1px solid var(--ts-border-red-400-4);background:linear-gradient(135deg,var(--ts-bg-red-500-5),var(--ts-bg-red-350-5));box-shadow:0 8px 20px var(--ts-shadow-red-500-a20) }
    @keyframes cancelBackdropIn { from{opacity:0}to{opacity:1} }
    @keyframes cancelModalIn { from{opacity:0;transform:translateY(16px) scale(.975)}to{opacity:1;transform:none} }
    @keyframes cancelWarningPulse { 0%,100%{transform:scale(1)}50%{transform:scale(1.055)} }
    @keyframes cancelWarningSweep { 0%,48%{transform:translateX(-115%)}78%,100%{transform:translateX(115%)} }
    @keyframes paymentSpin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .payment-alert, .payment-alert::after, .alert-icon, .processing-orbit span,.cancel-modal-overlay,.cancel-modal,.cancel-modal::before,.warning-symbol { animation: none; } }
    @media (max-width: 700px) { .renewal-summary { grid-template-columns: 1fr; } .renewal-summary .btn-primary { width: 100%; }.cancel-modal{padding:24px 17px 18px;overflow-y:auto}.loss-grid,.cancel-modal-actions{grid-template-columns:1fr}.access-deadline{align-items:flex-start;flex-direction:column;gap:4px} }
  `]
})
export class SuscripcionComponent implements OnInit, OnDestroy {

  subscription    = signal<Subscription | null>(null);
  payments        = signal<SubscriptionPayment[]>([]);
  paymentsTotal   = signal(0);
  paymentsPage    = signal(1);
  paymentsPages   = signal(1);
  paymentsLimit   = signal(25);
  isLoading       = signal(true);
  isActionLoading = signal(false);
  errorMsg        = signal('');
  actionMsg       = signal('');
  actionSuccess   = signal(false);
  cancelModalStep = signal<0 | 1 | 2 | 3>(0);
  cancellationError = signal('');
  showCancellationPassword = signal(false);
  cancellationPassword = '';
  cancellationReason = '';
  plans = signal<any[]>([]);
  selectedPlan = signal<any | null>(null);
  selectedPlanCode = signal('');
  showPlanChange = signal(false);
  paymentPurpose = signal<'plan_change' | 'renewal' | 'trial_upgrade'>('plan_change');
  paymentStage = signal<'form' | 'starting' | 'verifying' | 'success' | 'error'>('form');
  paymentProgress = signal(0);
  paymentStatusMessage = signal('');
  paymentFormError = signal('');
  safePaymentUrl = signal<SafeResourceUrl | null>(null);
  cardholderName = '';
  cardNumber = '';
  cardExpiry = '';
  cardCvv = '';
  advanceMonths = 1;
  private paymentStartedAt = 0;

  // Campo del formulario resaltado en rojo tras un intento de pago fallido
  // — se limpia solo en cuanto el usuario empieza a corregirlo.
  invalidField = signal<InvalidPaymentField | null>(null);
  // Formulario de tarjeta compartido: captura y sanea; esta pantalla conserva
  // los valores, la validación y el envío.
  @ViewChild(CardPaymentFormComponent) private cardForm?: CardPaymentFormComponent;

  clearFieldError(field: InvalidPaymentField): void {
    if (this.invalidField() === field) this.invalidField.set(null);
  }

  onCardChange(value: CardFormValue): void {
    this.cardNumber = value.cardNumber;
    this.cardholderName = value.cardholderName;
    this.cardExpiry = value.expireDate;
    this.cardCvv = value.cvc;
  }

  private focusInvalidField(field: InvalidPaymentField): void {
    this.invalidField.set(field);
    this.cardForm?.focus(field);
  }
  private paymentPollTimer?: number;

  // Conversión de prueba → definitiva: pasos previos al pago (datos + OTP),
  // ya que la cuenta de prueba nunca verificó su correo.
  trialConversionStage = signal<'profile' | 'otp' | 'payment'>('profile');
  trialProfileLoading = signal(false);
  trialProfileError = signal('');
  trialProfileLoaded = false;
  trialProfileForm: { name: string; company: string; email: string; phone: string } = { name: '', company: '', email: '', phone: '' };
  private trialVerificationId = '';
  trialOtpCode = '';

  private bodyOverflowBeforePaymentModal = '';

  constructor(
    private subscriptionService: SubscriptionService,
    private sanitizer: DomSanitizer,
    private auth: AuthService,
    private notifications: NotificationService,
    private themeService: ThemeService,
    private cooldown: ActionCooldownService,
  ) {
    // Sin esto, el panel de fondo (que en el admin ya usa overflow:hidden
    // en <html>, pero su contenedor interno de scroll sigue activo) podía
    // seguir moviéndose detrás del modal de pago mientras el iframe de
    // APay estaba abierto — mismo ajuste que el modal de pago del cliente.
    effect(() => {
      const open = this.showPlanChange();
      if (open) {
        this.bodyOverflowBeforePaymentModal = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this.bodyOverflowBeforePaymentModal;
      }
    });
  }

  ngOnInit(): void {
    this.loadSubscription();
    this.loadPayments();
    this.subscriptionService.getPublicPlans().subscribe({ next: ({ plans }) => this.plans.set(plans), error: () => {} });
  }

  loadSubscription(): void {
    this.isLoading.set(true);
    this.subscriptionService.getSubscription().subscribe({
      next: (res) => {
        this.subscription.set(res.subscription);
        this.selectedPlanCode.set(res.subscription.plan);
        this.isLoading.set(false);
      },
      error: (err) => {
        const msg = friendlyError(err, 'Error al cargar la suscripción.');
        this.errorMsg.set(msg);
        this.notifications.error(msg);
        this.isLoading.set(false);
      },
    });
  }

  loadPayments(page = 1): void {
    this.subscriptionService.getPaymentHistory(page, this.paymentsLimit()).subscribe({
      next: (res) => {
        this.payments.set(res.payments);
        this.paymentsTotal.set(res.total);
        this.paymentsPage.set(res.page);
        this.paymentsPages.set(res.pages);
      },
      error: () => {},
    });
  }

  goToPaymentsPage(page: number): void {
    if (page < 1 || page > this.paymentsPages() || page === this.paymentsPage()) return;
    this.loadPayments(page);
  }

  changePlan(newPlan: string): void {
    const current = this.subscription();
    if (!current || newPlan === current.plan) return;
    this.isActionLoading.set(true);
    this.actionMsg.set('');
    this.subscriptionService.changePlan(newPlan).subscribe({
      next: (res) => {
        this.actionMsg.set(res.msg);
        this.actionSuccess.set(true);
        this.notifications.success(res.msg);
        this.loadSubscription();
        this.isActionLoading.set(false);
      },
      error: (err) => {
        const msg = friendlyError(err, 'Error al cambiar el plan.');
        this.actionMsg.set(msg);
        this.actionSuccess.set(false);
        this.notifications.error(msg);
        this.isActionLoading.set(false);
      },
    });
  }

  currentPlan(): any | null {
    const subscription: any = this.subscription();
    const code = subscription?.plan;
    return this.plans().find(plan => plan.code === code) || subscription?.planDefinition || null;
  }

  selectedPlanPreview(currentPlanCode: string): any | null {
    const selected = this.selectedPlanCode();
    return selected && selected !== currentPlanCode ? this.plans().find(plan => plan.code === selected) || null : null;
  }

  enabledBenefits(plan: any | null): string[] {
    if (!plan) return [];
    return Object.entries(plan.benefits || {})
      .filter(([, value]) => this.isEnabledBenefit(value))
      .map(([key]) => this.benefitLabel(key));
  }

  private isEnabledBenefit(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private benefitLabel(key: string): string {
    const labels: Record<string, string> = {
      extras: 'Servicios adicionales', schedules: 'Horarios personalizados', pricing_tiers: 'Tarifas por duración',
      advanced_statistics: 'Estadísticas avanzadas', advance_statistics: 'Estadísticas avanzadas', advanced_statics: 'Estadísticas avanzadas', advance_statics: 'Estadísticas avanzadas',
      reports_export: 'Exportación de reportes', featured_listing: 'Publicación destacada', priority_support: 'Soporte prioritario',
      executive_support: 'Soporte ejecutivo', automated_confirmations: 'Confirmaciones automáticas', recurring_reservations: 'Reservas recurrentes',
      api_access: 'Acceso a la API', webhooks: 'Integraciones y automatizaciones', custom_branding: 'Identidad visual personalizada', custom_domain: 'Dominio personalizado',
      personalized_site: 'Sitio web personalizado', custom_site_url: 'URL propia para tu sitio',
    };
    return labels[key] || key.replace(/[_-]+/g, ' ').replace(/\bapi\b/gi, 'API').replace(/\bvip\b/gi, 'VIP').replace(/\b\w/g, char => char.toUpperCase());
  }

  openPlanChange(code: string): void {
    if (!this.cooldown.consume('suscripcion-plan-change')) return;
    const plan = this.plans().find(item => item.code === code);
    if (!plan || plan.code === this.subscription()?.plan) return;
    this.selectedPlan.set(plan);
    this.cardholderName = '';
    this.cardNumber = '';
    this.cardExpiry = '';
    this.cardCvv = '';
    this.paymentPurpose.set('plan_change');
    this.resetPaymentFlow();
    this.showPlanChange.set(true);
  }

  openTrialUpgrade(code: string): void {
    const plan = this.plans().find(item => item.code === code);
    if (!plan) return;
    this.selectedPlan.set(plan);
    this.cardholderName = '';
    this.cardNumber = '';
    this.cardExpiry = '';
    this.cardCvv = '';
    this.paymentPurpose.set('trial_upgrade');
    this.resetPaymentFlow();
    this.trialConversionStage.set('profile');
    this.trialProfileLoaded = false;
    this.trialProfileError.set('');
    this.showPlanChange.set(true);
    this.loadTrialProfile();
  }

  private loadTrialProfile(): void {
    this.trialProfileLoading.set(true);
    this.subscriptionService.getTrialProfile().subscribe({
      next: ({ profile }) => {
        this.trialProfileForm = { ...profile };
        this.trialProfileLoaded = true;
        this.trialProfileLoading.set(false);
      },
      error: err => {
        this.trialProfileError.set(friendlyError(err, 'No se pudieron cargar tus datos.'));
        this.trialProfileLoading.set(false);
      },
    });
  }

  submitTrialProfile(): void {
    this.trialProfileError.set('');
    this.trialProfileLoading.set(true);
    const payload = {
      name: this.trialProfileForm.name.trim(),
      company: this.trialProfileForm.company.trim(),
      email: this.trialProfileForm.email.trim().toLowerCase(),
      phone: this.trialProfileForm.phone.trim(),
    };
    this.subscriptionService.updateTrialProfile(payload).subscribe({
      next: ({ profile }) => {
        this.trialProfileForm = { ...profile };
        this.sendTrialOtp();
      },
      error: err => {
        this.trialProfileError.set(friendlyError(err, 'No se pudieron guardar los datos.'));
        this.trialProfileLoading.set(false);
      },
    });
  }

  sendTrialOtp(): void {
    this.trialProfileError.set('');
    this.trialProfileLoading.set(true);
    this.trialOtpCode = '';
    this.subscriptionService.sendEmailVerification(this.trialProfileForm.email, this.trialProfileForm.name).subscribe({
      next: ({ verificationId }) => {
        this.trialVerificationId = verificationId;
        this.trialConversionStage.set('otp');
        this.trialProfileLoading.set(false);
      },
      error: err => {
        this.trialProfileError.set(friendlyError(err, 'No se pudo enviar el código de verificación.'));
        this.trialProfileLoading.set(false);
      },
    });
  }

  sanitizeTrialOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.trialOtpCode = input.value.replace(/\D/g, '').slice(0, 6);
  }

  verifyTrialOtp(): void {
    this.trialProfileError.set('');
    this.trialProfileLoading.set(true);
    this.subscriptionService.verifyEmailCode(this.trialVerificationId, this.trialOtpCode).subscribe({
      next: () => {
        this.trialProfileLoading.set(false);
        this.trialConversionStage.set('payment');
      },
      error: err => {
        this.trialProfileError.set(friendlyError(err, 'El código es incorrecto o expiró.'));
        this.trialProfileLoading.set(false);
      },
    });
  }

  backToTrialProfile(): void {
    this.trialProfileError.set('');
    this.trialConversionStage.set('profile');
  }

  showCardFormStep(): boolean {
    return this.paymentPurpose() !== 'trial_upgrade' || this.trialConversionStage() === 'payment';
  }

  isTrialAccount(sub: Subscription): boolean {
    // El plan también debe ser 'gratis': un plan pagado real puede conservar
    // un trial_ends_at viejo de cuando la cuenta empezó como prueba -- sin
    // este chequeo esta pantalla (y la superposición de bloqueo, que
    // embebe este mismo componente) muestra "Cuenta de prueba" / "Prueba
    // finalizada" para un cliente que en realidad paga un plan real.
    return sub.plan === 'gratis' && (sub.status === 'trial' || (sub.status === 'expired' && !!sub.trial_ends_at));
  }

  trialDaysRemainingLabel(sub: Subscription): string {
    if (sub.status === 'expired') return 'Tu prueba finalizó.';
    if (!sub.trial_ends_at) return 'Cuenta de prueba activa.';
    const days = Math.ceil((new Date(sub.trial_ends_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    if (days <= 0) return 'Tu prueba vence hoy.';
    if (days === 1) return 'Queda 1 día de prueba.';
    return `Quedan ${days} días de prueba.`;
  }

  openRenewalPayment(): void {
    if (!this.cooldown.consume('suscripcion-renewal')) return;
    const plan = this.currentPlan();
    if (!plan || !['active', 'expired', 'inactive'].includes(String(this.subscription()?.status))) return;
    this.selectedPlan.set(plan);
    this.cardholderName = '';
    this.cardNumber = '';
    this.cardExpiry = '';
    this.cardCvv = '';
    this.paymentPurpose.set('renewal');
    this.resetPaymentFlow();
    this.showPlanChange.set(true);
  }

  async paySubscriptionPayment(): Promise<void> {
    if (!this.cooldown.consume('suscripcion-pay')) return;
    const plan = this.selectedPlan();
    const exp = this.cardExpiry.replace(/\D/g, '');
    const card = this.cardNumber.replace(/\D/g, '');
    const holder = this.cardholderName.replace(/\s+/g, ' ').trim();
    if (!plan) return;
    if (!/^[\p{L}]+(?: [\p{L}]+)*$/u.test(holder) || holder.length < 2) {
      this.paymentFormError.set('El nombre del titular solo puede contener letras y espacios.');
      this.focusInvalidField('cardholderName');
      return;
    }
    if (!/^\d{13,19}$/.test(card)) {
      this.paymentFormError.set('El número de tarjeta debe contener entre 13 y 19 dígitos.');
      this.focusInvalidField('cardNumber');
      return;
    }
    if (!this.isValidExpiry(exp)) {
      this.paymentFormError.set('Ingresa una fecha de vencimiento vigente en formato MM/AA.');
      this.focusInvalidField('expiry');
      return;
    }
    if (!/^\d{3,4}$/.test(this.cardCvv)) {
      this.paymentFormError.set('El código de seguridad debe contener 3 o 4 dígitos.');
      this.focusInvalidField('cvc');
      return;
    }

    this.cardholderName = holder;
    this.paymentFormError.set('');
    this.invalidField.set(null);
    this.actionMsg.set('');
    this.isActionLoading.set(true);
    this.paymentStartedAt = Date.now();
    this.paymentStage.set('starting');
    this.paymentProgress.set(15);
    this.paymentStatusMessage.set('Validando la información y conectando de forma segura con APay…');
    const purpose = this.paymentPurpose();
    const isRenewal = purpose === 'renewal';
    const isTrialUpgrade = purpose === 'trial_upgrade';
    const reference = isRenewal
      ? `renewal:${this.subscription()?.id}:${this.advanceMonths}`
      : (isTrialUpgrade ? `trial-upgrade:${this.subscription()?.id}:${plan.plan_id}` : `plan-change:${plan.plan_id}`);
    const description = isRenewal
      ? `Renovacion anticipada de ${this.advanceMonths} meses del ${plan.name}`
      : (isTrialUpgrade ? `Contratación de cuenta de prueba - ${plan.name}` : `Cambio al plan ${plan.name}`);
    this.subscriptionService.createPlanChangePayment({ monto: this.paymentTotal(), card_number: card, cvv: this.cardCvv, exp_date: `${exp.slice(2)}${exp.slice(0, 2)}`, cardholderName: holder, product_api: 'tusala_subscription', description, payment_reference: reference, ambiente: '1' }).subscribe({
      next: ({ idLink, url }) => {
        this.setGatewayUrl(url);
        this.paymentProgress.set(45);
        this.paymentStatusMessage.set('APay recibió la solicitud. Registrando la operación…');
        let confirm$;
        if (isRenewal) confirm$ = this.subscriptionService.confirmRenewalPayment(idLink, this.advanceMonths);
        else if (isTrialUpgrade) confirm$ = this.subscriptionService.confirmTrialUpgrade(plan.code, idLink);
        else confirm$ = this.subscriptionService.confirmPlanChange(plan.code, idLink);
        confirm$.subscribe({
        next: () => {
          this.paymentStage.set('verifying');
          this.paymentProgress.set(62);
          this.paymentStatusMessage.set('Esperando la confirmación segura del banco…');
          this.waitForSubscriptionPayment(idLink, isRenewal);
        },
        error: err => this.paymentError(err),
      });
      },
      error: err => this.paymentError(err),
    });
  }

  private waitForSubscriptionPayment(transactionId: string, isRenewal: boolean, attempts = 0): void {
    this.subscriptionService.getPaymentStatus(transactionId).subscribe({
      next: ({ status }) => {
        if (status === 'succeeded') {
          const message = isRenewal
            ? `Tu suscripción fue extendida por ${this.advanceMonths} ${this.advanceMonths === 1 ? 'mes' : 'meses'} sin alterar el ciclo contratado.`
            : (this.paymentPurpose() === 'trial_upgrade'
              ? '¡Listo! Tu cuenta ya está activa y tu prueba ha finalizado correctamente.'
              : 'El pago fue confirmado y tu nuevo plan ya está activo.');
          const finish = () => {
            this.paymentPollTimer = undefined;
            this.paymentProgress.set(100);
            this.paymentStatusMessage.set(message);
            this.paymentStage.set('success');
            this.actionSuccess.set(true);
            this.actionMsg.set(message);
            this.notifications.success(message);
            if (this.paymentPurpose() === 'trial_upgrade') this.auth.trialExpired.set(false);
            // Cualquier pago de suscripción confirmado (renovación, cambio de
            // plan o el paso de prueba a plan pagado) implica que el plan
            // vuelve a estar al día -- limpiar esta señal aunque no viniera
            // de esa causa es un no-op inofensivo si ya estaba en false.
            this.auth.subscriptionRestricted.set(false);
            this.loadSubscription();
            this.loadPayments();
            this.isActionLoading.set(false);
          };
          const remaining = Math.max(0, 0 - (Date.now() - this.paymentStartedAt));
          if (remaining > 0) {
            this.paymentPollTimer = window.setTimeout(finish, remaining);
          } else {
            finish();
          }
          return;
        }
        if (status === 'failed' || attempts >= 20) { this.paymentError({ error: { msg: status === 'failed' ? 'El pago fue rechazado.' : 'El pago sigue en verificación. Consulta el historial antes de intentarlo de nuevo.' } }); return; }
        this.paymentProgress.set(Math.min(94, 64 + attempts * 2));
        this.paymentStatusMessage.set(attempts < 6 ? 'APay está procesando la transacción…' : 'Confirmando el resultado con el banco…');
        this.paymentPollTimer = window.setTimeout(() => this.waitForSubscriptionPayment(transactionId, isRenewal, attempts + 1), 1500);
      }, error: err => this.paymentError(err),
    });
  }

  private paymentError(err: any): void {
    this.clearPaymentPollTimer();
    const message = friendlyError(err, 'No fue posible procesar el pago.');
    this.paymentStatusMessage.set(message);
    this.paymentStage.set('error');
    this.paymentProgress.set(0);
    this.safePaymentUrl.set(null);
    this.actionSuccess.set(false);
    this.isActionLoading.set(false);
    this.notifications.error(message);
  }

  ngOnDestroy(): void {
    this.clearPaymentPollTimer();
  }

  private resetPaymentFlow(): void {
    this.clearPaymentPollTimer();
    this.paymentStage.set('form');
    this.paymentProgress.set(0);
    this.paymentStatusMessage.set('');
    this.paymentFormError.set('');
    this.safePaymentUrl.set(null);
  }

  private clearPaymentPollTimer(): void {
    if (this.paymentPollTimer !== undefined) {
      window.clearTimeout(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
    }
  }

  private isValidExpiry(value: string): boolean {
    if (!/^\d{4}$/.test(value)) return false;
    const month = Number(value.slice(0, 2));
    const year = 2000 + Number(value.slice(2));
    const now = new Date();
    if (month < 1 || month > 12) return false;
    return (year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1))
      && year <= now.getFullYear() + 20;
  }

  private setGatewayUrl(rawUrl?: string): void {
    if (!rawUrl) return;
    try {
      const url = new URL(rawUrl, window.location.origin);
      const local = ['localhost', '127.0.0.1'].includes(url.hostname) && ['localhost', '127.0.0.1'].includes(window.location.hostname);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('La pasarela devolvió una URL inválida.');
      }
      if (url.protocol === 'http:' && !local) {
        url.protocol = 'https:';
      }
      this.safePaymentUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url.toString()));
    } catch {
      this.safePaymentUrl.set(null);
      this.paymentStatusMessage.set('No se pudo mostrar la ventana de APay. Intenta nuevamente.');
    }
  }

  isPaymentProcessing(): boolean {
    return this.paymentStage() === 'starting' || this.paymentStage() === 'verifying';
  }

  /** La pasarela (iframe de APay) permanece visible hasta que el usuario cierra
   * el modal manualmente, en vez de reemplazarse por un mensaje de éxito aparte. */
  showGatewayPanel(): boolean {
    return this.isPaymentProcessing() || this.paymentStage() === 'success';
  }

  canClosePaymentModal(): boolean {
    return !this.isPaymentProcessing();
  }

  closePaymentModal(): void {
    if (!this.canClosePaymentModal()) return;
    this.showPlanChange.set(false);
    this.safePaymentUrl.set(null);
  }

  paymentResultTitle(): string {
    return this.paymentPurpose() === 'renewal' ? 'Renovación confirmada' : 'Plan actualizado correctamente';
  }

  /** Dentro del modal "Gestionar negocio" de Super Admin. */
  get businessContextMode(): boolean {
    return this.auth.isBusinessContextMode();
  }

  cancelSubscription(): void {
    if (this.cancelModalStep() !== 3 || this.isActionLoading()) return;
    const password = this.cancellationPassword;
    const reason = this.cancellationReason.trim();
    // En "Gestionar negocio" la cancelación la registra el super admin
    // (el servidor no le pide la contraseña del propietario).
    if (!password && !this.businessContextMode) {
      this.cancellationError.set('Ingresa tu contraseña actual para confirmar que eres el propietario.');
      this.notifications.error(this.cancellationError());
      return;
    }
    if (reason.length < 10 || reason.length > 500) {
      this.cancellationError.set('Explica el motivo utilizando entre 10 y 500 caracteres.');
      this.notifications.error(this.cancellationError());
      return;
    }
    this.isActionLoading.set(true);
    this.actionMsg.set('');
    this.cancellationError.set('');
    this.subscriptionService.cancelSubscription(password, reason).subscribe({
      next: (res) => {
        this.actionMsg.set(res.msg);
        this.actionSuccess.set(true);
        this.notifications.success(res.msg);
        this.resetCancellationFlow();
        this.loadSubscription();
        this.isActionLoading.set(false);
      },
      error: (err) => {
        const message = friendlyError(err, 'No fue posible validar la solicitud. Conserva tu suscripción y contacta a soporte de TuSala.');
        this.cancellationError.set(message);
        this.actionSuccess.set(false);
        this.isActionLoading.set(false);
        this.notifications.error(message);
      },
    });
  }

  openCancellation(): void {
    if (this.isActionLoading()) return;
    this.resetCancellationFlow();
    this.cancelModalStep.set(1);
  }

  openCancellationVerification(): void {
    if (this.isActionLoading()) return;
    this.cancellationError.set('');
    this.cancelModalStep.set(3);
  }

  closeCancellation(): void {
    if (this.isActionLoading()) return;
    this.resetCancellationFlow();
  }

  private resetCancellationFlow(): void {
    this.cancelModalStep.set(0);
    this.cancellationPassword = '';
    this.cancellationReason = '';
    this.cancellationError.set('');
    this.showCancellationPassword.set(false);
  }

  getPlanName(plan: string): string {
    return PLAN_NAMES[plan] ?? plan;
  }

  getStatusLabel(status: string): string {
    return STATUS_LABELS[status] ?? status;
  }

  getPaymentStatusLabel(status: string): string {
    const labels: Record<string, string> = { succeeded: 'Aprobado', pending: 'En verificación', failed: 'Rechazado' };
    return labels[status] ?? status;
  }

  downloadReceipt(payment: SubscriptionPayment): void {
    if (!this.canDownloadReceipt(payment)) return;
    this.subscriptionService.downloadOwnSubscriptionReceipt(payment.payment_id).subscribe({
      next: file => {
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = `comprobante-suscripcion-TSL-SUB-${String(payment.payment_id).padStart(8, '0')}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.actionSuccess.set(false);
        this.actionMsg.set('No fue posible descargar el comprobante de este pago.');
        this.notifications.error('No fue posible descargar el comprobante de este pago.');
      },
    });
  }

  isRenewalWindow(): boolean {
    const renewalAt = this.subscription()?.renewal_at;
    if (!renewalAt || this.subscription()?.status !== 'active') return false;
    const daysRemaining = (new Date(renewalAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
    return daysRemaining >= 0 && daysRemaining <= 3;
  }

  normalizeMonthsInput(): void {
    const value = Math.trunc(Number(this.advanceMonths));
    this.advanceMonths = Number.isFinite(value) ? Math.min(60, Math.max(1, value)) : 1;
  }

  preventInvalidMonthsKey(event: KeyboardEvent): void {
    const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'];
    if (allowedControlKeys.includes(event.key) || ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()))) {
      return;
    }
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  sanitizeMonthsPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = String(event.clipboardData?.getData('text') || '').trim();
    if (!/^\d+$/.test(pasted)) return;
    const value = Number(pasted);
    this.advanceMonths = Math.min(60, Math.max(1, value));
  }

  spacesLimitLabel(plan: any | null, fullSentence = true): string {
    const limit = Number(plan?.limits?.max_spaces);
    if (!Number.isFinite(limit)) return fullSentence ? 'Cantidad de espacios no configurada.' : 'espacios no configurados';
    if (limit >= 9999) return fullSentence ? 'Publicación de espacios ilimitada.' : 'espacios ilimitados';
    return fullSentence ? `Hasta ${limit} espacios publicados.` : `hasta ${limit} espacios`;
  }

  trialSpaceLimitLabel(): string {
    const limit = Number(this.currentPlan()?.limits?.max_spaces);
    if (!Number.isFinite(limit)) return 'Cantidad de espacios de prueba no configurada.';
    return `Hasta ${limit} ${limit === 1 ? 'espacio publicado' : 'espacios publicados'} durante la prueba.`;
  }

  accessEndsAt(subscription: Subscription): string | null {
    if (!['cancelled', 'expired', 'inactive'].includes(subscription.status)) return null;
    return subscription.expires_at || subscription.renewal_at || null;
  }

  renewalAlertTitle(): string {
    const days = this.renewalDaysRemaining();
    if (days === 0) return 'Tu renovación vence hoy.';
    if (days === 1) return 'Solo falta 1 día para tu próximo pago.';
    return `Faltan ${days} días para tu próximo pago.`;
  }

  renewalDaysRemaining(): number {
    const renewalAt = this.subscription()?.renewal_at;
    if (!renewalAt) return 0;
    return Math.max(0, Math.ceil((new Date(renewalAt).getTime() - Date.now()) / 86400000));
  }

  renewalTotal(): number {
    return Number(this.subscription()?.custom_price_monthly
      ?? this.subscription()?.agreed_price_monthly
      ?? this.currentPlan()?.price_monthly
      ?? 0) * this.advanceMonths;
  }

  hasCustomization(subscription: Subscription | null): boolean {
    return !!subscription && (subscription.custom_max_spaces !== null && subscription.custom_max_spaces !== undefined
      || subscription.custom_price_monthly !== null && subscription.custom_price_monthly !== undefined
      || !!String(subscription.custom_pricing_notes || '').trim());
  }

  customizedSpacesLabel(subscription: Subscription): string {
    const limit = Number(subscription.custom_max_spaces ?? this.currentPlan()?.limits?.max_spaces ?? 0);
    return subscription.plan === 'premium' || limit >= 9999 ? 'Ilimitados' : `${limit} espacios`;
  }

  effectiveMonthlyPrice(subscription: Subscription): number {
    return Number(subscription.custom_price_monthly
      ?? subscription.agreed_price_monthly
      ?? this.currentPlan()?.price_monthly
      ?? 0);
  }

  paymentTotal(): number {
    return this.paymentPurpose() === 'renewal' ? this.renewalTotal() : Number(this.selectedPlan()?.price_monthly || 0);
  }

  projectedRenewalDate(): Date | null {
    const renewalAt = this.subscription()?.renewal_at;
    if (!renewalAt) return null;
    const result = new Date(renewalAt);
    const startDate = this.subscription()?.starts_at ? new Date(this.subscription()!.starts_at) : result;
    const day = startDate.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + this.advanceMonths);
    result.setDate(Math.min(day, new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()));
    return result;
  }

  paymentCoverageLabel(payment: SubscriptionPayment): string {
    const explicitStart = payment.coverage_starts_at ? new Date(payment.coverage_starts_at) : null;
    const explicitEnd = payment.coverage_ends_at ? new Date(payment.coverage_ends_at) : null;
    const format = (value: Date) => new Intl.DateTimeFormat('es-SV', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(value);
    if (explicitStart && explicitEnd && !Number.isNaN(explicitStart.getTime()) && !Number.isNaN(explicitEnd.getTime())) {
      return `${format(explicitStart)} — ${format(explicitEnd)}`;
    }
    const fallbackStart = new Date(payment.payment_date || payment.created_at);
    if (Number.isNaN(fallbackStart.getTime())) return 'Periodo no disponible';
    const months = Math.max(1, Number(payment.billing_months || 1));
    const fallbackEnd = new Date(fallbackStart);
    fallbackEnd.setMonth(fallbackEnd.getMonth() + months);
    fallbackEnd.setDate(fallbackEnd.getDate() - 1);
    return `${format(fallbackStart)} — ${format(fallbackEnd)}`;
  }

  canDownloadReceipt(payment: SubscriptionPayment): boolean {
    return payment.status === 'succeeded';
  }

  formatDateWithDay(value: string | Date | null | undefined): string {
    if (!value) return '—';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const parts = new Intl.DateTimeFormat('es-SV', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).formatToParts(date);
    const byType = (type: string) => parts.find(part => part.type === type)?.value || '';
    const weekday = byType('weekday');
    const day = byType('day');
    const month = byType('month');
    const year = byType('year');
    return `${weekday} ${day} de ${month} de ${year}`.trim();
  }

  getPaymentConcept(payment: SubscriptionPayment): string {
    if (payment.payment_concept) return payment.payment_concept;
    const reference = String((payment as any).payment_reference || '');
    if (reference.startsWith('pending:')) return 'Contratación de suscripción';
    if (reference.startsWith('plan-change:')) return 'Cambio de suscripción';
    if (reference.startsWith('renewal:')) return 'Pago por uso (renovación mensual)';
    return 'Pago de suscripción';
  }

  private readonly paymentStatusColors: Record<string, string> = {
    succeeded: '#43d392',
    pending: '#f2c66e',
    failed: '#f06a73',
  };

  paymentStatusColor(status: string): string {
    return toneForTheme(this.paymentStatusColors[status] || '#aebed0', this.themeService.theme());
  }

  paymentStatusColorSoft(status: string): string {
    const hex = this.paymentStatusColor(status);
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, .12)`;
  }
}
